import {
  DIRECT_LINE_NAME,
  DYNAMIC_CALLER_ID_NAME,
  DYNAMIC_CALLER_ID_NUMBER,
} from '@components/OutboundCallerIdSelection/constants';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import useInboxState from '@modules/InboxModule/hooks/useInboxState';
import { inboxActions } from '@modules/InboxModule/state';
import { useCallback, useEffect, useState } from 'react';
import { DIRECT_LINE } from 'src/constants/tenantSelection';
import { useTenantOutboundConfig } from './useTenantOutboundConfig';
import { AgentCallerIdDetails } from './types';

export const useAgentLine = ({
  tenantId,
  onLoadingChange,
  onChange,
  isDynamic,
}: {
  tenantId?: string;
  onLoadingChange?: (loading: boolean) => void;
  onChange: (data: AgentCallerIdDetails) => void;
  isDynamic?: boolean;
}) => {
  const { user } = useAuth();
  const { selectedCallerIdForOutboundCall } = useInboxState();
  const directLine = user?.personalLine?.phoneNumber;
  const tenantKey = tenantId ?? DIRECT_LINE;
  const isTenantSelected = tenantId !== DIRECT_LINE;
  const [selectedLine, setSelectedLine] = useState<{
    callerId: string;
    callerName?: string;
  } | null>(null);
  const {
    outgoingCallerId,
    enableCustomOutboundCallerId,
    isPermissionToUseDID,
    loadingCallerId,
    error,
  } = useTenantOutboundConfig({
    selectedTenantId: tenantId,
  });
  const canUsePersistedCallerId =
    !!tenantId && (isTenantSelected || (isPermissionToUseDID && !!directLine));
  const persistedCallerId = canUsePersistedCallerId
    ? selectedCallerIdForOutboundCall[tenantKey]
    : undefined;
  const isMultipleLines = isTenantSelected && !!outgoingCallerId && isDynamic;

  useEffect(() => {
    onLoadingChange?.(loadingCallerId);
  }, [loadingCallerId, onLoadingChange]);

  const calculateSelectedLine = useCallback(() => {
    if (persistedCallerId) {
      return persistedCallerId;
    }

    if (!isTenantSelected) {
      if (isPermissionToUseDID && directLine) {
        return {
          callerId: directLine,
          callerName: DIRECT_LINE_NAME,
        };
      }

      if (!isPermissionToUseDID) {
        return {
          callerId: '',
          callerName: 'No permissions to use direct line',
        };
      }

      return {
        callerId: '',
        callerName: 'No direct line setup for this agent',
      };
    }

    if (outgoingCallerId) {
      return {
        callerId: outgoingCallerId ?? '',
        callerName: '',
      };
    } else {
      return {
        callerId: DYNAMIC_CALLER_ID_NUMBER,
        callerName: DYNAMIC_CALLER_ID_NAME,
      };
    }
  }, [
    persistedCallerId,
    isTenantSelected,
    isPermissionToUseDID,
    directLine,
    outgoingCallerId,
  ]);

  const onLineChange = useCallback(
    (data: AgentCallerIdDetails, options: { persist?: boolean } = {}) => {
      setSelectedLine(data);
      if (options.persist) {
        inboxActions.setCallerIdForOutboundCall(tenantKey, data);
      }

      if (data?.callerId) {
        onChange({
          callerId: data.callerId,
          callerName: data.callerName ?? undefined,
        });
      }
    },
    [onChange, tenantKey],
  );

  useEffect(() => {
    if (!loadingCallerId) {
      const newSelectedLine = calculateSelectedLine();
      onLineChange(newSelectedLine);
    }
  }, [calculateSelectedLine, loadingCallerId, onLineChange]);

  return {
    selectedLine,
    showChangeButton: !!enableCustomOutboundCallerId || isMultipleLines,
    outgoingCallerId,
    enableCustomOutboundCallerId,
    isPermissionToUseDID,
    loadingCallerId,
    onLineChange,
    error,
  };
};
